import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=logged out, obj=user
  const [family, setFamily] = useState(null); // current family {id,name,invite_code,role}

  const loadFamily = useCallback(async () => {
    try {
      const { data } = await api.get("/families/current");
      setFamily(data);
    } catch {
      setFamily(null);
    }
  }, []);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("fv_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      if (data.active_family_id) await loadFamily();
      setUser(data);
    } catch {
      localStorage.removeItem("fv_token");
      setUser(false);
    }
  }, [loadFamily]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const finishAuth = useCallback(async (data) => {
    localStorage.setItem("fv_token", data.token);
    if (data.user.active_family_id) await loadFamily();
    setUser(data.user);
  }, [loadFamily]);

  const login = async (email, password, captcha) => {
    const { data } = await api.post("/auth/login", {
      email, password, captcha_id: captcha?.id, captcha_answer: captcha?.answer,
    });
    if (data.token) { await finishAuth(data); return { done: true }; }
    return data; // { mfa_required, mfa_token } | { mfa_setup_required, setup_token }
  };

  const verifyMfa = async (mfa_token, code) => {
    const { data } = await api.post("/auth/mfa/verify", { mfa_token, code });
    await finishAuth(data);
    return data;
  };

  const setupMfa = async (setup_token) => (await api.post("/auth/mfa/setup", { setup_token })).data;

  const enableMfa = async (setup_token, code) => (await api.post("/auth/mfa/enable", { setup_token, code })).data;

  const commitAuth = finishAuth; // used after showing backup codes on forced enrollment

  const register = async (name, email, password, captcha) => {
    const ref_code = localStorage.getItem("fv_ref") || undefined;
    const { data } = await api.post("/auth/register", {
      name, email, password, ref_code,
      captcha_id: captcha?.id, captcha_answer: captcha?.answer,
    });
    if (ref_code) localStorage.removeItem("fv_ref");
    if (data.pending_approval) return { pending: true };
    localStorage.setItem("fv_token", data.token);
    setUser(data.user);
    return { pending: false, user: data.user };
  };

  const logout = () => {
    localStorage.removeItem("fv_token");
    sessionStorage.removeItem("fv_tenant_confirmed");
    setUser(false);
    setFamily(null);
  };

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    if (data.active_family_id) await loadFamily();
    else setFamily(null);
    setUser(data);
  }, [loadFamily]);

  return (
    <AuthContext.Provider
      value={{ user, family, login, verifyMfa, setupMfa, enableMfa, commitAuth, register, logout, refreshUser, loadFamily, setFamily }}
    >
      {children}
    </AuthContext.Provider>
  );
}
